const PRIVATE_KEY_MARKERS = [
  '-----BEGIN OPENSSH PRIVATE KEY-----',
  '-----BEGIN PRIVATE KEY-----',
  '-----BEGIN RSA PRIVATE KEY-----',
  '-----BEGIN EC PRIVATE KEY-----',
  '-----BEGIN DSA PRIVATE KEY-----',
];

const PUBLIC_KEY_PREFIXES = ['ssh-ed25519', 'ssh-rsa'];

export const validatePublicKey = (value) => {
  const key = (value || '').trim();
  if (!key) return 'Paste or upload a public key.';
  if (PRIVATE_KEY_MARKERS.some((marker) => key.includes(marker))) {
    return 'That looks like a private key. Cloud Watcher only accepts the .pub file.';
  }
  const lines = key.split(/\r?\n/).filter(Boolean);
  if (lines.length !== 1 || !PUBLIC_KEY_PREFIXES.some((prefix) => key.startsWith(`${prefix} `))) {
    return 'Use one Ed25519 or RSA OpenSSH public key from a .pub file.';
  }
  const parts = key.split(/\s+/);
  if (parts.length < 2 || !/^[A-Za-z0-9+/]+={0,2}$/.test(parts[1])) {
    return 'The public key is not in valid OpenSSH format.';
  }
  return null;
};

export const normalizeSshIdentity = (payload = {}) => {
  const enveloped = Object.prototype.hasOwnProperty.call(payload, 'identity') || Object.prototype.hasOwnProperty.call(payload, 'ssh_identity');
  const record = payload.identity || payload.ssh_identity || (enveloped ? null : payload);
  const devices = payload.device_keys || payload.devices || record?.device_keys || record?.devices || [];
  if (!record && !enveloped) return null;
  return {
    ...(record || {}),
    status: record?.status || (record?.disabled_at ? 'disabled' : record ? 'active' : 'unenrolled'),
    device_keys: devices.map((device) => ({
      ...device,
      id: device.id || device.device_key_id,
      label: device.label || device.name || 'Unnamed device',
      fingerprint: device.fingerprint || device.public_key_fingerprint || 'Fingerprint unavailable',
      algorithm: device.algorithm || device.key_type,
      status: device.status || (device.revoked_at ? 'revoked' : device.verified_at ? 'verified' : 'pending_verification'),
      revision: device.revision ?? 0,
    })),
  };
};

export const normalizeChallenge = (payload = {}, device = null) => {
  const challenge = payload.challenge || payload.proof_challenge || payload;
  return {
    id: challenge.id || challenge.challenge_id,
    value: challenge.challenge || challenge.value || challenge.payload || '',
    namespace: challenge.namespace || 'cloudwatcher',
    expires_at: challenge.expires_at || challenge.expiry,
    fingerprint: challenge.fingerprint,
    device,
  };
};

export const deviceStatus = (status) => ({
  verified: { label: 'Verified', tone: 'emerald' },
  active: { label: 'Verified', tone: 'emerald' },
  pending: { label: 'Needs verification', tone: 'amber' },
  pending_verification: { label: 'Needs verification', tone: 'amber' },
  revoked: { label: 'Revoked', tone: 'zinc' },
  replaced: { label: 'Replaced', tone: 'zinc' },
  disabled: { label: 'Disabled', tone: 'red' },
}[status] || { label: 'Unavailable', tone: 'red' });

export const isUsableDevice = (device) => ['verified', 'active'].includes(device?.status) && !device?.revoked_at;

export const challengeFilename = (challengeId) => `cloudwatcher-challenge-${String(challengeId || 'proof').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 48) || 'proof'}.txt`;

export const proofCommand = (namespace = 'cloudwatcher', filename = 'cloudwatcher-challenge.txt') => {
  const safeNamespace = /^[a-zA-Z0-9._-]{1,64}$/.test(namespace) ? namespace : 'cloudwatcher';
  const safeFilename = /^[a-zA-Z0-9._-]{1,96}$/.test(filename) ? filename : 'cloudwatcher-challenge.txt';
  return `ssh-keygen -Y sign -f ~/.ssh/id_ed25519 -n ${safeNamespace} ${safeFilename}`;
};

export const readableDate = (value, fallback = 'Never') => {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};
